import { useState } from 'react';
import { trpc } from '../../api/trpc';
import { AgentCard } from './AgentCard';
import { CreateAgentDialog } from './CreateAgentDialog';

type TabFilter = 'all' | 'custom' | 'prebuilt';

interface AgentListItem {
  id: string;
  name: string;
  description: string | null;
  isPreBuilt: boolean;
  status: string;
  version: number;
  runCount?: number;
  creatorName?: string;
  lastInvokedAt: string | null;
}

interface Props {
  onSelectAgent: (agentId: string) => void;
  onBack: () => void;
}

export function AgentsListInline({ onSelectAgent, onBack: _onBack }: Props) {
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { data: agents } = trpc.taskAgents.agentList.useQuery({ filter: activeTab });

  const allAgents = (agents ?? []) as AgentListItem[];
  const customAgents = allAgents.filter((a: AgentListItem) => !a.isPreBuilt);
  const prebuiltAgents = allAgents.filter((a: AgentListItem) => a.isPreBuilt);
  const creatingAgents = allAgents.filter((a: AgentListItem) => a.status === 'creating');

  return (
    <>
    <div className="page-scroll">
      <div className="page-inner">
        <div className="page-header">
          <div className="page-title">Task Agents</div>
          <button
            className="btn-primary"
            style={{ padding: '8px 16px', fontSize: '13px' }}
            onClick={() => setShowCreateDialog(true)}
          >
            Create Agent
          </button>
        </div>

        <div className="page-tabs">
          <button className={`page-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
            All<span className="page-tab-count">{allAgents.length}</span>
          </button>
          <button className={`page-tab ${activeTab === 'custom' ? 'active' : ''}`} onClick={() => setActiveTab('custom')}>
            Custom
          </button>
          <button className={`page-tab ${activeTab === 'prebuilt' ? 'active' : ''}`} onClick={() => setActiveTab('prebuilt')}>
            Pre-built
          </button>
        </div>

        {creatingAgents.map((agent: AgentListItem) => (
          <AgentCard key={agent.id} agent={agent} onClick={() => onSelectAgent(agent.id)} />
        ))}

        {(activeTab === 'all' || activeTab === 'custom') && customAgents.length > 0 && (
          <>
            {activeTab === 'all' && <div className="section-label">Custom</div>}
            {customAgents.filter((a: AgentListItem) => a.status !== 'creating').map((agent: AgentListItem) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onClick={() => onSelectAgent(agent.id)}
                onRun={() => {}}
                onEdit={() => onSelectAgent(agent.id)}
                canEdit={true}
              />
            ))}
          </>
        )}

        {(activeTab === 'all' || activeTab === 'prebuilt') && prebuiltAgents.length > 0 && (
          <>
            {activeTab === 'all' && <div className="section-label" style={{ marginTop: 20 }}>Pre-built</div>}
            {prebuiltAgents.map((agent: AgentListItem) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onClick={() => onSelectAgent(agent.id)}
                onRun={() => {}}
                canEdit={false}
              />
            ))}
          </>
        )}

        {allAgents.length === 0 && (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 14 }}>
            No task agents yet. Create one or check back after setup.
          </div>
        )}
      </div>
    </div>

    {showCreateDialog && (
      <CreateAgentDialog
        onClose={() => setShowCreateDialog(false)}
        onCreated={(agentId) => { setShowCreateDialog(false); onSelectAgent(agentId); }}
      />
    )}
    </>
  );
}
